import {
  AccountCircle as AccountCircleIcon,
  AssignmentTurnedInOutlined,
  AssignmentTurnedInRounded,
  Email as EmailIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PaymentOutlined as PaymentIcon,
} from '@mui/icons-material';
import { Badge, Box, Hidden, List, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Label } from '@/components';
import {
  Modals,
  NavRoutes,
  NEW_FEATURE_ALERTS,
  Routes,
  RoutesConfig,
  RouteType,
} from '@/constants';
import { useNewFeatureAlert } from '@/hooks';
import {
  DiscoverActiveIcon,
  DiscoverIcon,
  LearningPathsActiveIcon,
  LearningPathsIcon,
  LibraryActiveIcon,
  LibraryIcon,
  MembersActiveIcon,
  MembersIcon,
  MyLumiQActiveIcon,
  MyLumiQIcon,
  ReportingIcon,
  TrackerActiveIcon,
  TrackerIcon,
} from '@/icons';
import { EVENT_CONSTANTS } from '@/services';
import {
  selectIsAdminUser,
  selectRecoveryEmailPending,
  selectSessionUserId,
  uiActions,
  useAppDispatch,
  useFetchBillingAdminOrgsQuery,
  useLazyFetchOrgBillingPortalLinkQuery,
  useLogoutMutation,
} from '@/store';
import { getRedirectSearchParams, SnackbarUtils } from '@/utils';

import { IncompleteNotificationBadge } from '../../../IncompleteNotificationBadge';
import { SidebarItem } from './components';

const SidebarIcons = {
  [NavRoutes.LEARNING_PATHS.PATH]: <LearningPathsIcon />,
  [NavRoutes.DISCOVER.PATH]: <DiscoverIcon />,
  [NavRoutes.LIBRARY.PATH]: <LibraryIcon />,
  [NavRoutes.TRACKER.PATH]: <TrackerIcon />,
  [NavRoutes.MY_LUMIQ.PATH]: (
    <IncompleteNotificationBadge>
      <MyLumiQIcon />
    </IncompleteNotificationBadge>
  ),
};

const SidebarActiveIcons = {
  [NavRoutes.LEARNING_PATHS.PATH]: <LearningPathsActiveIcon />,
  [NavRoutes.DISCOVER.PATH]: <DiscoverActiveIcon />,
  [NavRoutes.LIBRARY.PATH]: <LibraryActiveIcon />,
  [NavRoutes.TRACKER.PATH]: <TrackerActiveIcon />,
  [NavRoutes.MY_LUMIQ.PATH]: (
    <IncompleteNotificationBadge>
      <MyLumiQActiveIcon />
    </IncompleteNotificationBadge>
  ),
};

const NewLabel = () => <Label text="New" />;

export const SidebarList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const userId = useSelector(selectSessionUserId);
  const isUserManagementAdmin = useSelector(selectIsAdminUser);

  const onListItemClick = (item: RouteType) => {
    dispatch(uiActions.toggleSidebarDrawer());
    navigate(item.PATH);
  };

  const reportsNavItemNewCSVLabel = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.REPORTS_NAV_ITEM_NEW_CSV_LABEL,
  );

  const filterLibraryNewLabel = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.LIBRARY_FILTER_REGION,
  );
  const contentAdminNewLabel = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.CONTENT_NAV_ITEM_NEW_LABEL,
  );

  // Compose a map of which sidebar items should have "New" labels
  const [sideBarEndIcons, setSideBarEndIcons] = useState<
    Record<string, React.ReactElement | undefined>
  >({});
  useEffect(() => {
    setSideBarEndIcons(current => ({
      ...current,
      [RoutesConfig.LIBRARY.NAME]: filterLibraryNewLabel.isVisible ? (
        <NewLabel />
      ) : undefined,
    }));
  }, [filterLibraryNewLabel.isVisible]);

  const [logout] = useLogoutMutation();
  const { data: billingAdminOrgs } = useFetchBillingAdminOrgsQuery(
    userId ? undefined : skipToken,
  );
  const [fetchBillingPortalLink, { isLoading: isFetchingBillingPortalLink }] =
    useLazyFetchOrgBillingPortalLinkQuery();

  const isBillingAdmin = (billingAdminOrgs?.length ?? 0) > 0;

  const recoveryEmailPending = useSelector(selectRecoveryEmailPending);
  const recoveryEmailPrompt = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.PROFILE_RECOVERY_EMAIL_PROMPT,
    { ttl: 7, isEnabled: recoveryEmailPending },
  );

  return (
    <List
      component="nav"
      sx={{
        pt: 0,
        pb: theme => `${theme.dimensions.miniPlayer.height.sm}px`,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        color: 'text.contrast',
      }}>
      {Object.values(NavRoutes).map(item => {
        const isSelected = pathname === item.PATH;
        const Icon = (isSelected ? SidebarActiveIcons : SidebarIcons)[
          item.PATH
        ];

        return (
          <SidebarItem
            key={item.PATH}
            Icon={Icon}
            name={item.NAME}
            isSelected={isSelected}
            onClick={() => {
              if (item.IS_PRIVATE && !userId) {
                dispatch(
                  uiActions.setActiveModal({
                    name: Modals.SIGN_UP,
                    params: {
                      referrer: {
                        component: EVENT_CONSTANTS.COMPONENT.SIDE_BAR,
                      },
                      redirect: item.PATH,
                    },
                  }),
                );
              } else {
                onListItemClick(item);
              }
            }}
            EndIcon={sideBarEndIcons[item.NAME]}
          />
        );
      })}
      <Hidden lgUp>
        <SidebarItem
          Icon={<EmailIcon />}
          name="Support"
          onClick={() => {
            dispatch(uiActions.toggleSidebarDrawer());
            window.Intercom?.('show');
          }}
        />
        {userId ? (
          <>
            <SidebarItem
              Icon={
                <Box sx={{ position: 'relative', display: 'flex' }}>
                  <AccountCircleIcon />
                  {recoveryEmailPrompt.isVisible && (
                    <Badge
                      badgeContent=" "
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          height: '16px',
                          minWidth: '16px',
                        },
                      }}
                    />
                  )}
                </Box>
              }
              name={RoutesConfig.PROFILE.NAME}
              isSelected={pathname === RoutesConfig.PROFILE.PATH}
              onClick={() => {
                onListItemClick(RoutesConfig.PROFILE);
                if (recoveryEmailPrompt.isVisible) {
                  recoveryEmailPrompt.dismiss();
                }
              }}
            />
            <SidebarItem
              Icon={<LogoutIcon />}
              name="Log Out"
              onClick={() => logout()}
            />
          </>
        ) : (
          <SidebarItem
            Icon={<LoginIcon />}
            name="Log In"
            onClick={() =>
              navigate({
                pathname: Routes.LOGIN,
                search: getRedirectSearchParams(),
              })
            }
          />
        )}
      </Hidden>
      {isUserManagementAdmin && (
        <>
          <Typography
            variant="body2"
            textTransform="uppercase"
            sx={{ color: 'text.light' }}
            mt={2}
            mb={1}
            px={2}>
            Admin Tools
          </Typography>
          <SidebarItem
            Icon={
              pathname === RoutesConfig.MEMBER_MANAGEMENT.PATH ? (
                <MembersActiveIcon />
              ) : (
                <MembersIcon />
              )
            }
            name="Members"
            isSelected={pathname === RoutesConfig.MEMBER_MANAGEMENT.PATH}
            onClick={() => {
              onListItemClick(RoutesConfig.MEMBER_MANAGEMENT);
            }}
          />
          <SidebarItem
            Icon={<ReportingIcon />}
            name="Reporting"
            isSelected={pathname === RoutesConfig.REPORTING.PATH}
            onClick={() => {
              onListItemClick(RoutesConfig.REPORTING);
              reportsNavItemNewCSVLabel.dismiss();
            }}
          />
          <SidebarItem
            Icon={
              pathname === RoutesConfig.ADMIN_CONTENT.PATH ? (
                <AssignmentTurnedInRounded />
              ) : (
                <AssignmentTurnedInOutlined />
              )
            }
            EndIcon={contentAdminNewLabel.isVisible && <NewLabel />}
            name="Content Curation"
            isSelected={pathname === RoutesConfig.ADMIN_CONTENT.PATH}
            onClick={() => {
              onListItemClick(RoutesConfig.ADMIN_CONTENT);
              contentAdminNewLabel.dismiss();
            }}
          />
          {isBillingAdmin && (
            <SidebarItem
              Icon={<PaymentIcon />}
              name="Billing"
              onClick={async () => {
                if (isFetchingBillingPortalLink) return;
                try {
                  const billingPortalLink = await fetchBillingPortalLink({
                    organizationId: billingAdminOrgs![0],
                    redirectUrl: window.location.href,
                  }).unwrap();
                  window.open(billingPortalLink, '_self');
                } catch {
                  SnackbarUtils.error(
                    'Failed to open billing portal. Please contact support.',
                  );
                }
              }}
            />
          )}
        </>
      )}
    </List>
  );
};
